import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Clock, Building2, Calendar, User, Award, Languages, FileText } from 'lucide-react';
import { supabaseBookingStorage, supabaseCustomerStorage, supabaseInstructorStorage } from '../../services/supabaseStorage';
import { searchCache, localCustomerStorage } from '../../services/localStorageCache';
import { courseSessionsService } from '../../services/courseSessionsService';
import { interpretersService } from '../../services/interpretersService';
import { QuotesService } from '../../services/quotesService';
import { supabase } from '../../lib/supabase';
import {
  sessionReferenceCode,
  bookingReferenceCode,
  buildSessionReferenceIndex,
  buildBookingReferenceIndex,
  trainingReferenceMatchesQuery,
  looksLikeTrainingReferenceQuery,
} from '../dashboard/trainingCalendarUtils';
import type { Customer } from '../../types/crm';
import { customerDisplayTitle, globalCustomerSearchDisplay } from '../../utils/customerRecordPersistence';
import { resolveCustomerByPersonName } from '../../utils/customerSearch';

type SearchResultType =
  | 'customer'
  | 'booking'
  | 'instructor'
  | 'interpreter'
  | 'certificate'
  | 'session'
  | 'invoice'
  | 'quote';

interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  action: () => void;
  context?: CachedSearchResult['context'];
}

interface CachedSearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle: string;
  context?: {
    customerId?: string;
    bookingId?: string;
    sessionId?: string;
    instructorId?: string;
    interpreterId?: string;
    certificateId?: string;
    quoteId?: string;
    focusTab?: string;
    activeTab?: string;
  };
}

interface GlobalSearchProps {
  onViewChange: (
    view:
      | 'dashboard'
      | 'certificates'
      | 'clients'
      | 'reminders'
      | 'reviews'
      | 'bookings'
      | 'quotes'
      | 'superadmin'
      | 'admin'
      | 'faq'
      | 'tasks'
      | 'financial'
      | 'emails'
      | 'ops'
      | 'process-numbers',
    context?: Record<string, unknown>
  ) => void;
  /** inline = header search; overlay = icon that opens a full-screen search panel (mobile) */
  variant?: 'inline' | 'overlay';
}

const SEARCH_LIMIT = 15;

export function GlobalSearch({ onViewChange, variant = 'inline' }: GlobalSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const syncDropdownPosition = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const rect = input.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const getIconForType = useCallback((type: SearchResultType) => {
    switch (type) {
      case 'customer':
        return <Building2 className="h-4 w-4 text-green-600" />;
      case 'booking':
        return <Calendar className="h-4 w-4 text-blue-600" />;
      case 'instructor':
        return <User className="h-4 w-4 text-purple-600" />;
      case 'interpreter':
        return <Languages className="h-4 w-4 text-teal-600" />;
      case 'certificate':
        return <Award className="h-4 w-4 text-emerald-600" />;
      case 'session':
        return <Calendar className="h-4 w-4 text-indigo-600" />;
      case 'invoice':
        return <FileText className="h-4 w-4 text-sky-600" />;
      case 'quote':
        return <FileText className="h-4 w-4 text-amber-600" />;
      default:
        return <Search className="h-4 w-4 text-slate-500" />;
    }
  }, []);

  const buildAction = useCallback(
    (item: CachedSearchResult) => {
      switch (item.type) {
        case 'customer':
          if (item.context?.customerId) {
            onViewChange('clients', { selectedCustomerId: item.context.customerId });
          }
          break;
        case 'booking':
          if (item.context?.bookingId) {
            onViewChange('bookings', { bookingId: item.context.bookingId });
          }
          break;
        case 'session':
        case 'invoice':
          if (item.context?.sessionId) {
            onViewChange('bookings', { sessionId: item.context.sessionId });
          }
          break;
        case 'instructor':
          if (item.context?.instructorId) {
            // Instructors are staff, not CRM clients — open Admin → Instructors
            onViewChange('admin', {
              activeTab: 'instructors',
              instructorId: item.context.instructorId,
            });
          }
          break;
        case 'interpreter':
          onViewChange('admin', { activeTab: 'interpreters', interpreterId: item.context?.interpreterId });
          break;
        case 'certificate':
          if (item.context?.customerId) {
            onViewChange('clients', {
              selectedCustomerId: item.context.customerId,
              focusTab: item.context.focusTab || 'certificates',
              certificateId: item.context.certificateId,
            });
          }
          break;
        case 'quote':
          if (item.context?.quoteId) {
            onViewChange('quotes', { quoteId: item.context.quoteId, customerId: item.context.customerId });
          }
          break;
      }
      setIsOpen(false);
    },
    [onViewChange]
  );

  const applyIcons = useCallback(
    (items: CachedSearchResult[]) =>
      items.map((item) => ({
        ...item,
        icon: getIconForType(item.type),
        action: () => buildAction(item),
      })),
    [buildAction, getIconForType]
  );

  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        if (variant === 'overlay') {
          setOverlayOpen(true);
        }
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
        setOverlayOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [variant]);

  useEffect(() => {
    if (variant === 'overlay' && overlayOpen) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
  }, [variant, overlayOpen]);

  useLayoutEffect(() => {
    if (!isOpen || variant === 'overlay') {
      setDropdownRect(null);
      return;
    }
    syncDropdownPosition();
    window.addEventListener('resize', syncDropdownPosition);
    window.addEventListener('scroll', syncDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', syncDropdownPosition);
      window.removeEventListener('scroll', syncDropdownPosition, true);
    };
  }, [isOpen, syncDropdownPosition, searchTerm, results.length, isLoading, variant]);

  const performSearch = useCallback(
    async (term: string) => {
      const trimmed = term.trim();
      const normalizedTerm = trimmed.toLowerCase();
      const includesTerm = (value: unknown) => String(value ?? '').toLowerCase().includes(normalizedTerm);

      if (!trimmed) {
        setResults([]);
        return;
      }

      const cacheKey = `global_v13_${normalizedTerm}`;
      const cachedResults = searchCache.get<CachedSearchResult[]>(cacheKey);
      const currentRequestId = ++requestIdRef.current;

      if (cachedResults) {
        setResults(applyIcons(cachedResults));
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }

      try {
        const searchQuotes =
          typeof QuotesService.searchForGlobal === 'function'
            ? QuotesService.searchForGlobal(trimmed, SEARCH_LIMIT)
            : Promise.resolve([]);
        const searchInterpreters =
          typeof interpretersService.searchForGlobal === 'function'
            ? interpretersService.searchForGlobal(trimmed, SEARCH_LIMIT)
            : Promise.resolve([]);

        const [bookings, customers, instructors, interpreters, quotes, sessions, invoiceSessions] =
          await Promise.all([
          supabaseBookingStorage.searchBookings(trimmed, SEARCH_LIMIT).catch(() => []),
          supabaseCustomerStorage.searchCustomers(trimmed, SEARCH_LIMIT).catch(() => []),
          supabaseInstructorStorage.searchInstructors(trimmed, SEARCH_LIMIT).catch(() => []),
          searchInterpreters.catch(() => []),
          searchQuotes.catch(() => []),
          courseSessionsService.listSessions().catch(() => []),
          courseSessionsService.searchSessionsByInvoiceNumber(trimmed, SEARCH_LIMIT).catch(() => []),
        ]);

        const sessionRefIndex = buildSessionReferenceIndex(sessions);
        const cached: CachedSearchResult[] = [];
        const seen = new Set<string>();

        const pushResult = (item: CachedSearchResult) => {
          const key = `${item.type}:${item.id}`;
          if (seen.has(key)) return;
          seen.add(key);
          cached.push(item);
        };

        let refScanBookings: Array<{
          id: string;
          courseName: string;
          courseDate: string;
          status: string;
          createdAt?: string;
        }> = [];
        if (looksLikeTrainingReferenceQuery(trimmed)) {
          refScanBookings = await supabaseBookingStorage.getBookings(1500, 0).catch(() => []);
        }
        const bookingRefIndex = buildBookingReferenceIndex(refScanBookings);

        const bookingMatchesRef = (bookingId: string) => {
          const hashRef = bookingReferenceCode(bookingId);
          const seq = bookingRefIndex.get(bookingId);
          const seqRef = bookingReferenceCode(bookingId, seq);
          return trainingReferenceMatchesQuery(trimmed, hashRef) || trainingReferenceMatchesQuery(trimmed, seqRef);
        };

        const sessionMatchesRef = (sessionId: string) => {
          const seq = sessionRefIndex.get(sessionId);
          if (seq == null) return false;
          const seqRef = sessionReferenceCode(sessionId, seq);
          return trainingReferenceMatchesQuery(trimmed, seqRef);
        };

        customers.forEach((customer) => {
          const { title, subtitle } = globalCustomerSearchDisplay(customer as Customer);
          pushResult({
            id: customer.id,
            type: 'customer',
            title,
            subtitle,
            context: { customerId: customer.id },
          });
        });

        const localCustomers = localCustomerStorage.getData<Customer>() || [];
        let localCustomerCount = 0;
        for (const customer of localCustomers) {
          if (localCustomerCount >= SEARCH_LIMIT) break;
          const customerMatches = [
            customer.companyName,
            customer.sageCode,
            customer.emailAddress,
            customer.contactPhoneNumber,
            customer.firstName,
            customer.surname,
            customer.industry,
          ].some(includesTerm);
          if (!customerMatches) continue;
          const { title, subtitle } = globalCustomerSearchDisplay(customer);
          const before = cached.length;
          pushResult({
            id: customer.id,
            type: 'customer',
            title,
            subtitle,
            context: { customerId: customer.id },
          });
          if (cached.length > before) localCustomerCount += 1;
        }

        bookings.forEach((booking) => {
          const ref = bookingReferenceCode(booking.id, bookingRefIndex.get(booking.id));
          pushResult({
            id: booking.id,
            type: 'booking',
            title: `${booking.courseName} · ${ref}`,
            subtitle: `${new Date(booking.courseDate).toLocaleDateString('en-GB')} · ${booking.status}`,
            context: { bookingId: booking.id },
          });
        });

        refScanBookings.forEach((booking) => {
          if (!bookingMatchesRef(booking.id)) return;
          const ref = bookingReferenceCode(booking.id, bookingRefIndex.get(booking.id));
          pushResult({
            id: booking.id,
            type: 'booking',
            title: `${booking.courseName} · ${ref}`,
            subtitle: `${new Date(booking.courseDate).toLocaleDateString('en-GB')} · ${booking.status}`,
            context: { bookingId: booking.id },
          });
        });

        instructors.forEach((instructor) => {
          pushResult({
            id: instructor.id,
            type: 'instructor',
            title: `${instructor.firstName} ${instructor.lastName}`.trim(),
            subtitle:
              ['Instructor', instructor.email, instructor.phone].filter(Boolean).join(' · ') ||
              'Instructor',
            context: { instructorId: instructor.id, activeTab: 'instructors' },
          });
        });

        interpreters.forEach((interpreter) => {
          const langs = [interpreter.language1, interpreter.language2, interpreter.language3].filter(Boolean).join(', ');
          pushResult({
            id: interpreter.id,
            type: 'interpreter',
            title: interpreter.name,
            subtitle: [interpreter.email, interpreter.phone, langs].filter(Boolean).join(' · ') || 'Interpreter',
            context: { interpreterId: interpreter.id, activeTab: 'interpreters' },
          });
        });

        quotes.forEach((quote) => {
          pushResult({
            id: quote.id,
            type: 'quote',
            title: `${quote.quoteNumber || 'Quote'} · ${quote.courseName}`,
            subtitle: `${quote.status}${quote.clientPO ? ` · PO ${quote.clientPO}` : ''}`,
            context: { quoteId: quote.id, customerId: quote.customerId },
          });
        });

        invoiceSessions.forEach((hit) => {
          const subtitleDate = hit.firstDate
            ? new Date(hit.firstDate).toLocaleDateString('en-GB')
            : 'Date TBC';
          const seatLabel = hit.seatCount === 1 ? '1 seat' : `${hit.seatCount} seats`;
          pushResult({
            id: `${hit.sessionId}::${hit.invoiceNumber}`,
            type: 'invoice',
            title: `${hit.courseName} · Inv. ${hit.invoiceNumber}`,
            subtitle: `${subtitleDate} · ${hit.language || 'Language not set'} · ${seatLabel}`,
            context: { sessionId: hit.sessionId },
          });
        });

        sessions.forEach((session) => {
          const displayRef = sessionReferenceCode(session.id, sessionRefIndex.get(session.id));
          const hashRef = sessionReferenceCode(session.id);
          const matchesRef = sessionMatchesRef(session.id);
          const refQuery = looksLikeTrainingReferenceQuery(trimmed);
          if (refQuery) {
            if (!matchesRef) return;
          } else {
            const haystack = [
              displayRef,
              hashRef,
              session.courseName,
              session.language,
              ...(session.dateBlocks || []).map((d) => d.date),
            ];
            if (!haystack.some(includesTerm) && !matchesRef) return;
          }
          const firstDate = session.dateBlocks?.[0]?.date;
          const subtitleDate = firstDate ? new Date(firstDate).toLocaleDateString('en-GB') : 'Date TBC';
          pushResult({
            id: session.id,
            type: 'session',
            title: `${session.courseName} · ${displayRef}`,
            subtitle: `${subtitleDate} · ${session.language || 'Language not set'}`,
            context: { sessionId: session.id },
          });
        });

        const localCustomersForCerts = localCustomerStorage.getData<Customer>() || [];
        const certSourceCustomers: Customer[] = [...localCustomersForCerts];

        // Also pull certificate matches from DB (local cache is often incomplete / cleared)
        try {
          const { data: certIds } = await supabase
            .rpc('customer_ids_matching_certificate_text', {
              p_q: trimmed,
              p_limit: SEARCH_LIMIT * 3,
            });
          if (Array.isArray(certIds) && certIds.length > 0) {
            const missing = certIds
              .map(String)
              .filter((id) => !certSourceCustomers.some((c) => c.id === id));
            if (missing.length) {
              const fromDb = await supabaseCustomerStorage.getCustomersByIds(missing).catch(() => []);
              for (const c of fromDb || []) certSourceCustomers.push(c as Customer);
            }
          }
        } catch {
          /* optional */
        }

        if (certSourceCustomers.length > 0) {
          let certificateCount = 0;
          for (const customer of certSourceCustomers) {
            if (!customer.generatedCertificates?.length) continue;
            for (const cert of customer.generatedCertificates) {
              const certMatches = [
                cert.id,
                cert.fileName,
                cert.attendeeName,
                cert.courseName,
                cert.courseDate,
                cert.expiryDate,
                cert.instructorName,
                cert.instructorRegNumber,
              ].some(includesTerm);
              if (!certMatches) continue;

              const attendeeIndividual = cert.attendeeName
                ? resolveCustomerByPersonName(cert.attendeeName, certSourceCustomers)
                : undefined;
              const profileCustomer = attendeeIndividual || customer;
              const certDate = cert.courseDate
                ? new Date(cert.courseDate).toLocaleDateString('en-GB')
                : 'Date unknown';

              const { title, subtitle } = globalCustomerSearchDisplay(profileCustomer);
              pushResult({
                id: profileCustomer.id,
                type: 'customer',
                title,
                subtitle: attendeeIndividual
                  ? subtitle
                  : `${subtitle} · Certificate: ${cert.attendeeName || cert.courseName}`,
                context: { customerId: profileCustomer.id },
              });

              pushResult({
                id: cert.id,
                type: 'certificate',
                title: `${cert.attendeeName} · ${cert.courseName}`,
                subtitle: `${customerDisplayTitle(customer)} · ${certDate}`,
                context: {
                  customerId: profileCustomer.id,
                  certificateId: cert.id,
                  focusTab: 'certificates',
                },
              });
              certificateCount += 1;
              if (certificateCount >= SEARCH_LIMIT) break;
            }
            if (certificateCount >= SEARCH_LIMIT) break;
          }
        }

        if (currentRequestId === requestIdRef.current) {
          setResults(applyIcons(cached));
          searchCache.set(cacheKey, cached);
        }

        if (trimmed && !recentSearches.includes(trimmed)) {
          const newRecent = [trimmed, ...recentSearches.slice(0, 4)];
          setRecentSearches(newRecent);
          localStorage.setItem('recentSearches', JSON.stringify(newRecent));
        }
      } catch (error) {
        console.error('Global search error:', error);
        if (currentRequestId === requestIdRef.current) {
          setResults([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [applyIcons, recentSearches]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void performSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) void performSearch(searchTerm);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  };

  const groupedResults = useMemo(() => {
    return {
      customers: results.filter((r) => r.type === 'customer'),
      bookings: results.filter((r) => r.type === 'booking'),
      sessions: results.filter((r) => r.type === 'session'),
      invoices: results.filter((r) => r.type === 'invoice'),
      quotes: results.filter((r) => r.type === 'quote'),
      instructors: results.filter((r) => r.type === 'instructor'),
      interpreters: results.filter((r) => r.type === 'interpreter'),
      certificates: results.filter((r) => r.type === 'certificate'),
    };
  }, [results]);

  const renderSection = (label: string, items: SearchResult[]) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div className="border-b border-slate-100 px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500">
          {label} ({items.length})
        </div>
        {items.map((result) => (
          <button
            key={`${result.type}-${result.id}`}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              result.action();
            }}
            className="search-result-enhanced hover-lift flex w-full items-center space-x-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
          >
            {result.icon}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-900">{result.title}</div>
              <div className="truncate text-sm text-slate-500">{result.subtitle}</div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const dropdownPanel = isOpen && dropdownRect ? (
    <div
      ref={dropdownRef}
      className="dropdown-enhanced scrollbar-enhanced animate-fade-in fixed z-[100000] max-h-[min(24rem,calc(100vh-6rem))] overflow-y-auto rounded-2xl border border-slate-200/80 bg-white shadow-2xl dark:border-slate-600 dark:bg-slate-900"
      style={{
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: dropdownRect.width,
      }}
      role="listbox"
      aria-label="Global search results"
    >
      {isLoading ? (
        <div className="p-4 text-center text-slate-500">
          <div className="loading-spinner-enhanced mx-auto mb-2" />
          Searching…
        </div>
      ) : searchTerm ? (
        results.length > 0 ? (
          <div className="py-2">
            {renderSection('Clients & companies', groupedResults.customers)}
            {renderSection('Invoices & courses', groupedResults.invoices)}
            {renderSection('Course sessions', groupedResults.sessions)}
            {renderSection('Bookings', groupedResults.bookings)}
            {renderSection('Quotes', groupedResults.quotes)}
            {renderSection('Instructors', groupedResults.instructors)}
            {renderSection('Interpreters', groupedResults.interpreters)}
            {renderSection('Certificates', groupedResults.certificates)}
          </div>
        ) : (
          <div className="p-4 text-center text-slate-500">No results found for &ldquo;{searchTerm}&rdquo;</div>
        )
      ) : (
        <div className="py-2">
          {recentSearches.length > 0 ? (
            <>
              <div className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500">Recent searches</div>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSearchTerm(search)}
                  className="flex w-full items-center space-x-3 px-4 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">{search}</span>
                </button>
              ))}
            </>
          ) : null}
          <div className="px-4 py-2 text-xs text-slate-400">
            Search clients, companies, individuals, invoice numbers, sessions, bookings, quotes, instructors, interpreters, or certificates
          </div>
        </div>
      )}
    </div>
  ) : null;

  const searchForm = (
    <form onSubmit={handleSearchSubmit} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search clients, invoice #, sessions… (Ctrl+K)"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            if (variant !== 'overlay') syncDropdownPosition();
          }}
          className={`input-enhanced focus-enhanced w-full min-w-0 rounded-2xl border border-slate-200/80 bg-white/95 py-2.5 pl-10 pr-10 text-sm shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-slate-300 hover:shadow-md focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-600 dark:bg-slate-900/95 ${
            variant === 'inline' ? 'max-w-md' : ''
          }`}
        />
        {searchTerm ? (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 transform text-slate-400 transition-colors hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </form>
  );

  if (variant === 'overlay') {
    const overlayPanel =
      overlayOpen && typeof document !== 'undefined' ? (
        <div className="fixed inset-0 z-[100000] flex flex-col bg-[rgb(var(--surface))] pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-2 border-b border-[rgb(var(--border))] px-3 py-3">
            <div className="min-w-0 flex-1">{searchForm}</div>
            <button
              type="button"
              onClick={() => {
                setOverlayOpen(false);
                setIsOpen(false);
                clearSearch();
              }}
              className="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-[rgb(var(--foreground))] hover:bg-[rgb(var(--surface-alt))]"
            >
              Cancel
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
            {isLoading ? (
              <div className="px-4 py-6 text-sm text-slate-500">Searching…</div>
            ) : results.length > 0 ? (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {results.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    type="button"
                    onClick={() => {
                      result.action();
                      setOverlayOpen(false);
                    }}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <span className="mt-0.5">{result.icon}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                        {result.title}
                      </span>
                      <span className="block truncate text-xs text-slate-500">{result.subtitle}</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : searchTerm.trim() ? (
              <div className="px-4 py-6 text-sm text-slate-500">No results</div>
            ) : recentSearches.length > 0 ? (
              <div>
                <div className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500">
                  Recent searches
                </div>
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSearchTerm(search)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">{search}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-sm text-slate-400">
                Search clients, Sage code, invoice #, sessions, bookings, quotes…
              </div>
            )}
          </div>
        </div>
      ) : null;

    return (
      <div className="relative" ref={searchRef}>
        <button
          type="button"
          onClick={() => {
            setOverlayOpen(true);
            setIsOpen(true);
          }}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] text-[rgb(var(--foreground))] hover:bg-[rgb(var(--surface-alt))]"
          aria-label="Search"
          title="Search (Ctrl+K)"
        >
          <Search className="h-5 w-5" />
        </button>
        {overlayPanel ? createPortal(overlayPanel, document.body) : null}
      </div>
    );
  }

  return (
    <div className="relative w-full min-w-0" ref={searchRef}>
      {searchForm}
      {typeof document !== 'undefined' && dropdownPanel ? createPortal(dropdownPanel, document.body) : null}
    </div>
  );
}
